import { useState, useMemo } from 'react';
import type { Lead, Turma, FormaPagamento } from '@/mocks/admin';
import EditarParticipanteModal from './EditarParticipanteModal';
import CustomSelect from './CustomSelect';

interface AdminFinanceiroProps {
  leads: Lead[];
  turmas: Turma[];
  onStatusChange: (leadId: string, status: Lead['status']) => void;
  onLeadUpdate?: (leadId: string, updates: Partial<Lead>) => void;
}

const VALOR_PADRAO = 9700;

const formaConfig: Record<FormaPagamento, { label: string; icon: string; color: string; bg: string }> = {
  pix:            { label: 'PIX',              icon: 'ri-links-line',      color: 'text-emerald-600', bg: 'bg-emerald-50' },
  boleto:         { label: 'Boleto',            icon: 'ri-file-line',       color: 'text-blue-600',    bg: 'bg-blue-50' },
  cartao_credito: { label: 'Cartão de Crédito', icon: 'ri-bank-card-line',  color: 'text-violet-600',  bg: 'bg-violet-50' },
  transferencia:  { label: 'Transferência',      icon: 'ri-exchange-line',   color: 'text-sky-600',     bg: 'bg-sky-50' },
};

const nivelColors: Record<number, string> = {
  1: 'bg-red-50 text-red-600',
  2: 'bg-orange-50 text-orange-600',
  3: 'bg-amber-50 text-amber-600',
  4: 'bg-emerald-50 text-emerald-600',
};

export default function AdminFinanceiro({ leads, turmas, onStatusChange, onLeadUpdate }: AdminFinanceiroProps) {
  const [leadEditando, setLeadEditando] = useState<Lead | null>(null);
  const [leadVisualizando, setLeadVisualizando] = useState<Lead | null>(null);

  const stats = useMemo(() => {
    const confirmados = leads.filter((l) => l.status === 'confirmado');
    const interessados = leads.filter((l) => l.status === 'interessado');
    const comDiagnostico = leads.filter((l) => l.diagnostico !== null);

    const totalPrevisto = confirmados.reduce((acc, l) => {
      return acc + (l.preco_programa ?? VALOR_PADRAO);
    }, 0);

    const totalSubsidio = confirmados.reduce((acc, l) => acc + (l.valor_subsidio ?? 0), 0);
    const totalEntrada = confirmados.reduce((acc, l) => acc + (l.valor_entrada ?? 0), 0);

    return {
      confirmados,
      confirmadosCount: confirmados.length,
      interessados,
      interessadosCount: interessados.length,
      comDiagnosticoCount: comDiagnostico.length,
      totalPrevisto,
      totalSubsidio,
      totalEntrada,
    };
  }, [leads]);

  const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const handleLeadUpdate = (updates: Partial<Lead>, novoPreco?: number) => {
    if (!leadEditando) return;
    if (novoPreco !== undefined) {
      onLeadUpdate?.(leadEditando.id, { ...updates, preco_programa: novoPreco });
    } else {
      onLeadUpdate?.(leadEditando.id, updates);
    }
    setLeadEditando(null);
  };

  return (
    <div className="space-y-8">

      {/* ── STATS FINANCEIROS ── */}
      <div className="grid grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Faturamento Previsto</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100">
              <i className="ri-bar-chart-2-line text-indigo-600 text-sm" />
            </div>
          </div>
          <p className="text-3xl font-black text-indigo-600">{formatCurrency(stats.totalPrevisto)}</p>
          <p className="text-gray-400 text-xs mt-1">
            {stats.confirmadosCount} participante{stats.confirmadosCount !== 1 ? 's' : ''}
            {stats.totalSubsidio > 0 && ` · subsídio: ${formatCurrency(stats.totalSubsidio)}`}
          </p>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Entradas Registradas</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100">
              <i className="ri-wallet-line text-sky-600 text-sm" />
            </div>
          </div>
          <p className="text-3xl font-black text-sky-600">{formatCurrency(stats.totalEntrada)}</p>
          <p className="text-gray-400 text-xs mt-1">valor de entrada pago</p>
        </div>
      </div>

      {/* ── STATS GERAIS ── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Confirmados</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100">
              <i className="ri-checkbox-circle-line text-emerald-600 text-sm" />
            </div>
          </div>
          <p className="text-4xl font-black text-emerald-600">{stats.confirmadosCount}</p>
          <p className="text-gray-400 text-xs mt-1">
            Subsídio total: {formatCurrency(stats.totalSubsidio)}
          </p>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Interessados TR60D</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100">
              <i className="ri-graduation-cap-line text-amber-600 text-sm" />
            </div>
          </div>
          <p className="text-4xl font-black text-amber-600">{stats.interessadosCount}</p>
          <p className="text-gray-400 text-xs mt-1">manifestaram interesse</p>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Com Diagnóstico</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100">
              <i className="ri-file-chart-line text-indigo-600 text-sm" />
            </div>
          </div>
          <p className="text-4xl font-black text-indigo-600">{stats.comDiagnosticoCount}</p>
          <p className="text-gray-400 text-xs mt-1">finalizaram o quiz</p>
        </div>
      </div>


      {/* ── TABELA DE CONFIRMADOS ── */}
      {stats.confirmados.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-gray-900 font-black text-base">Participantes Confirmados</h3>
              <p className="text-gray-400 text-xs mt-1">
                {stats.confirmadosCount} participante{stats.confirmadosCount !== 1 ? 's' : ''} ·
                Total previsto: {formatCurrency(stats.totalPrevisto)}
                {stats.totalSubsidio > 0 && ` (subsídio: ${formatCurrency(stats.totalSubsidio)})`}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50">
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-6 py-3">Lead</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Turma</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Nível</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Parcelas</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Valor Final</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Forma PGTO</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stats.confirmados.map((lead) => {
                  const turma = turmas.find((t) => t.id === lead.turmaId);
                  const numParcelas = lead.num_parcelas ?? 1;
                  const valor_final = (lead.preco_programa ?? VALOR_PADRAO);
                  const fp = formaConfig[lead.forma_pagamento ?? 'pix'];

                  return (
                    <tr key={lead.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100 flex-shrink-0">
                            <span className="text-emerald-700 text-xs font-black">{lead.nome.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="text-gray-900 text-sm font-bold">{lead.nome}</p>
                            <p className="text-gray-400 text-xs">{lead.empresa}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-gray-700 text-xs font-semibold">{turma?.nome ?? '—'}</span>
                      </td>
                      <td className="px-4 py-4">
                        {lead.diagnostico ? (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${nivelColors[lead.diagnostico.nivelNumero]}`}>
                            N{lead.diagnostico.nivelNumero}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-gray-700 text-xs font-semibold">{numParcelas}x</span>
                          {numParcelas > 1 && (
                            <span className="text-xs text-gray-400">
                              {formatCurrency((valor_final - (lead.valor_entrada ?? 0)) / numParcelas)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-gray-900 text-xs font-bold">{formatCurrency(valor_final)}</p>
                          {(lead.valor_subsidio ?? 0) > 0 && (
                            <p className="text-xs text-emerald-600">− {formatCurrency(lead.valor_subsidio ?? 0)} subsídio</p>
                          )}
                          {(lead.valor_entrada ?? 0) > 0 && (
                            <p className="text-xs text-sky-600">+ {formatCurrency(lead.valor_entrada)} entrada</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {lead.forma_pagamento ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${fp.bg} ${fp.color}`}>
                            <i className={`${fp.icon} text-xs`} />
                            {fp.label}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setLeadVisualizando(lead)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer"
                            title="Ver detalhes"
                          >
                            <i className="ri-eye-line text-indigo-400 text-sm" />
                          </button>
                          <button
                            onClick={() => setLeadEditando(lead)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                            title="Editar participante"
                          >
                            <i className="ri-edit-line text-amber-500 text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* ── INTERESSADOS TR60D ── */}
      {stats.interessados.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-gray-900 font-black text-base">Interessados TR60D</h3>
            <p className="text-gray-400 text-xs mt-1">Leads que manifestaram interesse — aguardando confirmação de pagamento</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50">
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-6 py-3">Lead</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Nível</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Origens</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-3">Data</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stats.interessados.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                          <span className="text-amber-700 text-xs font-black">{lead.nome.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-gray-900 text-sm font-bold">{lead.nome}</p>
                          <p className="text-gray-400 text-xs">{lead.empresa}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {lead.diagnostico ? (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${nivelColors[lead.diagnostico.nivelNumero]}`}>
                          N{lead.diagnostico.nivelNumero}
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        {lead.origens.map((o) => (
                          <span key={o} className={`text-xs font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                            o === 'programa' ? 'bg-emerald-50 text-emerald-600' : 'bg-violet-50 text-violet-600'
                          }`}>
                            {o === 'programa' ? 'Interessado TR60D' : 'Diagnóstico'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <CustomSelect
                        options={[
                          { value: 'novo', label: 'Novo' },
                          { value: 'contatado', label: 'Contatado' },
                          { value: 'interessado', label: 'Interessado' },
                          { value: 'confirmado', label: 'Confirmado' },
                          { value: 'desistiu', label: 'Desistiu' },
                        ]}
                        value={lead.status}
                        onChange={(v) => onStatusChange(lead.id, v as Lead['status'])}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-gray-500 text-xs whitespace-nowrap">
                        {new Date(lead.dataCadastro).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setLeadVisualizando(lead)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer"
                          title="Ver detalhes"
                        >
                          <i className="ri-eye-line text-indigo-400 text-sm" />
                        </button>
                        <button
                          onClick={() => setLeadEditando(lead)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                          title="Editar participante"
                        >
                          <i className="ri-edit-line text-amber-500 text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── EMPTY STATE ── */}
      {stats.confirmadosCount === 0 && stats.interessadosCount === 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
            <i className="ri-money-dollar-circle-line text-gray-400 text-2xl" />
          </div>
          <h3 className="text-gray-900 font-black text-lg mb-2">Nenhum dado financeiro ainda</h3>
          <p className="text-gray-400 text-sm">Os leads confirmados e interessados aparecerão aqui.</p>
        </div>
      )}

      {/* ── MODAL VISUALIZAÇÃO (readonly) ── */}
      {leadVisualizando && (
        <div
          className="fixed inset-0 bg-black/50 z-[99999] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setLeadVisualizando(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-100 flex-shrink-0">
                    <i className="ri-eye-line text-indigo-600 text-lg" />
                  </div>
                  <div>
                    <h2 className="text-gray-900 font-black text-base">Detalhes do Lead</h2>
                    <p className="text-gray-400 text-xs">{leadVisualizando.nome}</p>
                  </div>
                </div>
                <button onClick={() => setLeadVisualizando(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-gray-400 text-lg" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Nome</p>
                  <p className="text-gray-900 text-sm font-semibold">{leadVisualizando.nome}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Empresa</p>
                  <p className="text-gray-900 text-sm font-semibold">{leadVisualizando.empresa || '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">E-mail</p>
                  <p className="text-gray-900 text-sm font-semibold">{leadVisualizando.email}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">WhatsApp</p>
                  <p className="text-gray-900 text-sm font-semibold">{leadVisualizando.whatsapp}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Status</p>
                  <p className="text-gray-900 text-sm font-semibold capitalize">{leadVisualizando.status}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Origens</p>
                  <p className="text-gray-900 text-sm font-semibold">{leadVisualizando.origens.join(', ')}</p>
                </div>
                {leadVisualizando.diagnostico && (
                  <>
                    <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                      <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Diagnóstico</p>
                      <p className="text-gray-700 text-sm font-semibold mb-1">
                        N{leadVisualizando.diagnostico.nivelNumero} — {leadVisualizando.diagnostico.nivelTitulo}
                      </p>
                      <p className="text-gray-400 text-xs">{leadVisualizando.diagnostico.percentualGeral}% de maturidade</p>
                    </div>
                  </>
                )}
                {leadVisualizando.forma_pagamento && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Forma de Pagamento</p>
                    <p className="text-gray-900 text-sm font-semibold capitalize">{leadVisualizando.forma_pagamento}</p>
                  </div>
                )}
                {leadVisualizando.num_parcelas && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Parcelas</p>
                    <p className="text-gray-900 text-sm font-semibold">{leadVisualizando.num_parcelas}x</p>
                  </div>
                )}
                {leadVisualizando.preco_programa && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Valor do Programa</p>
                    <p className="text-gray-900 text-sm font-semibold">{formatCurrency(leadVisualizando.preco_programa)}</p>
                  </div>
                )}
                {leadVisualizando.valor_subsidio ? (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Subsídio SEBRAE</p>
                    <p className="text-emerald-600 text-sm font-semibold">− {formatCurrency(leadVisualizando.valor_subsidio)}</p>
                  </div>
                ) : null}
                {leadVisualizando.valor_entrada ? (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Entrada</p>
                    <p className="text-sky-600 text-sm font-semibold">{formatCurrency(leadVisualizando.valor_entrada)}</p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setLeadVisualizando(null)}
                className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm py-3 rounded-xl transition-colors cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL EDIÇÃO PARTICIPANTE ── */}
      {leadEditando && (
        <EditarParticipanteModal
          lead={leadEditando}
          precoPadrao={VALOR_PADRAO}
          onClose={() => setLeadEditando(null)}
          onSave={handleLeadUpdate}
        />
      )}
    </div>
  );
}